import React, { useState, useEffect, useRef } from 'react';
import '../Css/AdminBlog.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faSave, faTimes, faHeading, faLink, faAlignLeft,
  faUser, faCalendar, faImage, faChevronLeft, faChevronRight, faExclamationTriangle,
  faToggleOn, faFileAlt, faImages, faFolderOpen
} from '@fortawesome/free-solid-svg-icons';

export default function AdminBlogs() {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [autoSlideEnabled, setAutoSlideEnabled] = useState({});
  const [user, setUser] = useState(null);
  const slideIntervals = useRef({});

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: null,
    gallery_images: [],
    category_id: '',
    author_id: '',
    status: 'draft',
    published_at: null
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser(payload);
        setFormData(prev => ({ ...prev, author_id: payload.user_id }));
      } catch (e) {
        toast.error('Invalid token');
      }
    }
    fetchBlogs();
    fetchCategories();
    return () => {
      Object.values(slideIntervals.current).forEach(clearInterval);
    };
  }, []);

  useEffect(() => {
    blogs.forEach(blog => {
      const images = getBlogImages(blog);
      if (images.length > 1 && autoSlideEnabled[blog.id]) {
        startAutoSlide(blog.id, images.length);
      } else {
        stopAutoSlide(blog.id);
      }
    });
    return () => {
      blogs.forEach(blog => stopAutoSlide(blog.id));
    };
  }, [blogs, autoSlideEnabled]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/blog.php/get`);
      const data = await response.json();
      if (data.success) {
        setBlogs(data.blogs);
        const initialIndexes = {};
        const initialAutoSlide = {};
        data.blogs.forEach(blog => {
          initialIndexes[blog.id] = 0;
          initialAutoSlide[blog.id] = true;
        });
        setCurrentImageIndex(initialIndexes);
        setAutoSlideEnabled(initialAutoSlide);
      }
    } catch (error) {
      toast.error('Error fetching blogs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/category.php/get?type=blog`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      toast.error('Error fetching categories');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        title: value,
        slug: generateSlug(value)
      }));
    } else if (name === 'featured_image') {
      const file = files[0];
      if (file) {
        setFeaturedImagePreview(URL.createObjectURL(file));
        setFormData(prev => ({ ...prev, featured_image: file }));
      }
    } else if (name === 'gallery_images') {
      const newFiles = Array.from(files);
      
      // Add new files to formData
      setFormData(prev => ({ 
        ...prev, 
        gallery_images: [...prev.gallery_images, ...newFiles] 
      }));
      
      // Create previews for new files
      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          setGalleryPreviews(prev => [...prev, {
            name: file.name,
            url: e.target.result,
            isExisting: false,
            file: file
          }]);
        };
        reader.readAsDataURL(file);
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeGalleryImage = (index) => {
    const imageToRemove = galleryPreviews[index];
    
    console.log('Removing image at index:', index);
    console.log('Image to remove:', imageToRemove);
    console.log('Is existing:', imageToRemove.isExisting);
    
    // Remove from preview array
    const updatedPreviews = galleryPreviews.filter((_, i) => i !== index);
    setGalleryPreviews(updatedPreviews);
    
    // If it's a NEW file (not existing in DB), also remove from formData.gallery_images
    if (!imageToRemove.isExisting) {
      // Count how many NEW files come BEFORE this index
      let newFileIndex = 0;
      for (let i = 0; i < index; i++) {
        if (!galleryPreviews[i].isExisting) {
          newFileIndex++;
        }
      }
      
      console.log('Removing new file at position:', newFileIndex);
      
      // Remove the file at that position from formData.gallery_images
      const updatedFiles = [...formData.gallery_images];
      updatedFiles.splice(newFileIndex, 1);
      setFormData({ ...formData, gallery_images: updatedFiles });
      
      console.log('Updated files count:', updatedFiles.length);
    } else {
      console.log('This is an existing DB image, will be excluded on submit');
    }
    
    console.log('Updated previews count:', updatedPreviews.length);
  };

  const removeFeaturedImageHandler = () => {
    setFeaturedImagePreview(null);
    setFormData(prev => ({ ...prev, featured_image: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug || !formData.content || !formData.category_id) {
      toast.error('Title, slug, content, and category are required');
      return;
    }

    const form = new FormData();
    form.append('title', formData.title);
    form.append('slug', formData.slug);
    form.append('content', formData.content);
    form.append('excerpt', formData.excerpt || '');
    form.append('category_id', formData.category_id);
    form.append('author_id', formData.author_id);
    form.append('status', formData.status);

    if (editMode) {
      form.append('_method', 'PUT');
    }

    // Featured image
    if (formData.featured_image instanceof File) {
      form.append('featured_image', formData.featured_image);
    }

    // ============ CRITICAL GALLERY IMAGES HANDLING ============
    if (editMode) {
      // For EDIT mode: preserve existing + add new
      const existingImagesToKeep = galleryPreviews
        .filter(preview => preview.isExisting)
        .map(preview => preview.name);
      
      console.log('=== EDIT MODE GALLERY DEBUG ===');
      console.log('Total gallery previews:', galleryPreviews.length);
      console.log('Existing images to keep:', existingImagesToKeep);
      console.log('New files to upload:', formData.gallery_images.length);
      
      // Send existing images to keep
      if (existingImagesToKeep.length > 0) {
        form.append('existing_gallery_images', JSON.stringify(existingImagesToKeep));
        console.log('Sent existing_gallery_images:', JSON.stringify(existingImagesToKeep));
      }
      
      // Send new images
      if (formData.gallery_images.length > 0) {
        formData.gallery_images.forEach((file, index) => {
          form.append('gallery_images[]', file);
          console.log(`Appending new image ${index + 1}:`, file.name);
        });
      }
    } else {
      // For CREATE mode: just add all new images
      console.log('=== CREATE MODE GALLERY DEBUG ===');
      console.log('New files to upload:', formData.gallery_images.length);
      
      if (formData.gallery_images.length > 0) {
        formData.gallery_images.forEach((file, index) => {
          form.append('gallery_images[]', file);
          console.log(`Appending new image ${index + 1}:`, file.name);
        });
      }
    }
    // ============ END CRITICAL SECTION ============

    const url = editMode
      ? `${API_URL}/blog.php/put/${selectedBlog.id}`
      : `${API_URL}/blog.php/post`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: form
      });
      const data = await response.json();

      if (data.success) {
        toast.success(editMode ? 'Blog updated' : 'Blog created');
        handleCloseModal();
        fetchBlogs();
      } else {
        toast.error(data.error || 'Operation failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Network error');
    }
  };

  const handleEdit = (blog) => {
    setEditMode(true);
    setSelectedBlog(blog);

    setFormData({
      title: blog.title,
      slug: blog.slug,
      content: blog.content,
      excerpt: blog.excerpt || '',
      category_id: blog.category_id,
      author_id: blog.author_id,
      status: blog.status,
      featured_image: null,
      gallery_images: [],
      published_at: blog.published_at
    });

    if (blog.featured_image) {
      setFeaturedImagePreview(`${API_URL}/uploads/${blog.featured_image}`);
    } else {
      setFeaturedImagePreview(null);
    }

    if (blog.gallery_images) {
      try {
        const existingGallery = JSON.parse(blog.gallery_images);
        const galleryPreviews = existingGallery.map(img => ({
          name: img,
          url: `${API_URL}/uploads/${img}`,
          isExisting: true
        }));
        setGalleryPreviews(galleryPreviews);
      } catch (e) {
        setGalleryPreviews([]);
      }
    } else {
      setGalleryPreviews([]);
    }

    setShowModal(true);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/blog.php/delete/${deleteId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Blog deleted');
        fetchBlogs();
      } else {
        toast.error(data.error);
      }
    } catch (error) {
      toast.error('Delete failed');
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedBlog(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      featured_image: null,
      gallery_images: [],
      category_id: '',
      author_id: user?.user_id || '',
      status: 'draft',
      published_at: null
    });
    setFeaturedImagePreview(null);
    setGalleryPreviews([]);
  };

  const getBlogImages = (blog) => {
    const images = [];
    if (blog.featured_image) {
      images.push(`${API_URL}/uploads/${blog.featured_image}`);
    }
    if (blog.gallery_images) {
      try {
        const gallery = JSON.parse(blog.gallery_images);
        gallery.forEach(img => images.push(`${API_URL}/uploads/${img}`));
      } catch (e) { }
    }
    return images.length > 0 ? images : ['https://via.placeholder.com/400x300?text=No+Image'];
  };

  const startAutoSlide = (blogId, total) => {
    if (slideIntervals.current[blogId]) clearInterval(slideIntervals.current[blogId]);
    slideIntervals.current[blogId] = setInterval(() => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [blogId]: (prev[blogId] + 1) % total
      }));
    }, 4000);
  };

  const stopAutoSlide = (blogId) => {
    if (slideIntervals.current[blogId]) {
      clearInterval(slideIntervals.current[blogId]);
      slideIntervals.current[blogId] = null;
    }
  };

  const nextImage = (blogId, total) => {
    const wasEnabled = autoSlideEnabled[blogId];
    if (wasEnabled) setAutoSlideEnabled(prev => ({ ...prev, [blogId]: false }));
    setCurrentImageIndex(prev => ({
      ...prev,
      [blogId]: (prev[blogId] + 1) % total
    }));
    if (wasEnabled) {
      setTimeout(() => setAutoSlideEnabled(prev => ({ ...prev, [blogId]: true })), 10000);
    }
  };

  const prevImage = (blogId, total) => {
    const wasEnabled = autoSlideEnabled[blogId];
    if (wasEnabled) setAutoSlideEnabled(prev => ({ ...prev, [blogId]: false }));
    setCurrentImageIndex(prev => ({
      ...prev,
      [blogId]: (prev[blogId] - 1 + total) % total
    }));
    if (wasEnabled) {
      setTimeout(() => setAutoSlideEnabled(prev => ({ ...prev, [blogId]: true })), 10000);
    }
  };

  const goToImage = (blogId, index) => {
    const wasAutoSlideEnabled = autoSlideEnabled[blogId];
    
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [blogId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [blogId]: index
    }));

    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [blogId]: true
        }));
      }, 10000);
    }
  };

  return (
    <div className="admin-blogs-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="admin-blogs-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="admin-blogs-heading">
                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                Manage Blogs
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="add-blog-btn" onClick={() => setShowModal(true)}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Blog
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Grid */}
      <div className="container py-5">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {blogs.map(blog => {
              const images = getBlogImages(blog);
              const currentIndex = currentImageIndex[blog.id] || 0;
              const hasMultipleImages = images.length > 1;
              
              return (
                <div key={blog.id} className="col-lg-4 col-md-6">
                  <div className="admin-blog-card">
                    <div className="admin-blog-image position-relative">
                      <img
                        src={images[currentIndex]}
                        alt={blog.title}
                        className="img-fluid"
                      />
                      {hasMultipleImages && (
                        <>
                          <button
                            className="slider-arrow slider-arrow-prev"
                            onClick={() => prevImage(blog.id, images.length)}
                          >
                            <FontAwesomeIcon icon={faChevronLeft} />
                          </button>
                          <button
                            className="slider-arrow slider-arrow-next"
                            onClick={() => nextImage(blog.id, images.length)}
                          >
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                          
                          <div className="image-count-badge">
                            <FontAwesomeIcon icon={faImages} className="me-1" />
                            {currentIndex + 1} / {images.length}
                          </div>
                        
                          
                          <div className="slider-dots">
  {images.map((_, idx) => (
    <button
      key={idx}
      className={`slider-dot ${idx === currentIndex ? 'active' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        goToImage(blog.id, idx);
      }}
    />
  ))}
</div>

                        </>
                      )}
                      <div className="image-category-badges">
                        {blog.category_name || 'Uncategorized'}
                      </div>
                    </div>

                    <div className="admin-blog-body">
                      <h5 className="admin-blog-title">{blog.title}</h5>
                      <p className="admin-blog-excerpt">{blog.excerpt || 'No excerpt'}</p>

                      <div className="admin-blog-meta">
                        <span className="meta-item">
                          <FontAwesomeIcon icon={faUser} /> {blog.author_name || 'Unknown'}
                        </span>
                        <span className="meta-item">
                          <FontAwesomeIcon icon={faCalendar} /> {new Date(blog.created_at).toLocaleDateString()}
                        </span>
                        <span className={`status-badge ${blog.status}`}>
                          {blog.status}
                        </span>
                      </div>
                    </div>

                    <div className="admin-blog-footer">
                      <button className="btn btn-outline-primary btn-sm" onClick={() => handleEdit(blog)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(blog.id)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-backdrop" onClick={handleCloseModal}>
          <div className="admin-modal-dialog modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Blog' : 'Add New Blog'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <form id="blogForm" onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Title
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faLink} className="me-1" />
                        Slug
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="slug"
                        value={formData.slug}
                        readOnly
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFolderOpen} className="me-1" />
                        Category
                      </label>
                      <select
                        className="form-select"
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faToggleOn} className="me-1" />
                        Status
                      </label>
                      <select
                        className="form-select"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>

                    <div className="col-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                        Excerpt
                      </label>
                      <textarea
                        className="form-control"
                        name="excerpt"
                        value={formData.excerpt}
                        onChange={handleInputChange}
                        rows="2"
                        placeholder="Short summary..."
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faAlignLeft} className="me-1" />
                        Content
                      </label>
                      <textarea
                        className="form-control"
                        name="content"
                        value={formData.content}
                        onChange={handleInputChange}
                        rows="8"
                        required
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Featured Image
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="featured_image"
                        accept="image/*"
                        onChange={handleInputChange}
                      />
                      {featuredImagePreview && (
                        <div className="mt-2 position-relative d-inline-block">
                          <img src={featuredImagePreview} alt="Preview" className="featured-image-preview" />
                          <button
                            type="button"
                            className="btn btn-danger btn-sm position-absolute top-0 end-0"
                            onClick={removeFeaturedImageHandler}
                            style={{ transform: 'translate(25%, -25%)' }}
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faImages} className="me-1" />
                        Gallery Images
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="gallery_images"
                        accept="image/*"
                        multiple
                        onChange={handleInputChange}
                      />
                      {galleryPreviews.length > 0 && (
                        <div className="gallery-preview-container mt-3">
                          {galleryPreviews.map((preview, idx) => (
                            <div key={idx} className="gallery-preview-item">
                              <img src={preview.url} alt={`Gallery ${idx + 1}`} className="gallery-image-preview" />
                              <button
                                type="button"
                                className="gallery-remove-btn"
                                onClick={() => removeGalleryImage(idx)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="blogForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update Blog' : 'Create Blog'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="admin-modal-backdrop">
          <div className="admin-modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this blog? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}